import {
  searchItemPath,
  searchResultSchema,
  searchTokens,
  SEARCH_MODULES,
  type ModuleKey,
  type SearchItem,
  type SearchTypeName,
} from '@fluxus/shared';
import { useQuery } from '@tanstack/react-query';
import {
  CalendarClock,
  FileText,
  Gauge,
  Rocket,
  Search as SearchIcon,
  SearchX,
  Target,
  type LucideIcon,
} from 'lucide-react';
import { useEffect, useId, useState, type KeyboardEvent } from 'react';
import { useNavigate } from 'react-router';
import { EmptyState, ErrorState, LoadingState } from '@/components/states/States';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Tooltip } from '@/components/ui/tooltip';
import { useCurrentProject } from '@/features/workspace/workspace';
import { canAccess } from '@/lib/auth/access';
import { useAuth, useCurrentUser } from '@/lib/auth/session';
import { cn } from '@/lib/cn';

const TYPE_ICONS: Record<SearchTypeName, LucideIcon> = {
  objective: Target,
  kpi: Gauge,
  initiative: Rocket,
  meeting: CalendarClock,
  document: FileText,
};

const isMac = () => /Macintosh|Mac OS X|iPhone|iPad/.test(navigator.userAgent);

/**
 * Busca global (T-203b, RF-43, PRD §11.28; DEC-069): botão no topo e atalho Ctrl+K (Cmd+K no Mac) abrindo a busca
 * no projeto aberto, com os resultados agrupados por tipo, navegação por teclado e o link para a tela de cada um.
 * Só aparece para quem vê alguma das telas onde a busca procura.
 */
export function GlobalSearch({ organizationId }: { organizationId: string }) {
  const user = useCurrentUser();
  const allowed = SEARCH_MODULES.some((m) => canAccess(user, m));
  const [open, setOpen] = useState(false);
  const shortcut = isMac() ? '⌘K' : 'Ctrl K';

  useEffect(() => {
    if (!allowed) return;
    const onKey = (e: globalThis.KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && !e.altKey && !e.shiftKey && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        // Com uma janela já aberta (formulário, detalhe ou a própria busca), o atalho não abre outra por cima.
        if (document.querySelector('[role="dialog"], [role="alertdialog"]')) return;
        setOpen(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [allowed]);

  if (!allowed) return null;
  return (
    <>
      <Tooltip content={`Buscar (${shortcut})`}>
        <Button
          variant="outline"
          aria-label="Buscar"
          aria-keyshortcuts={isMac() ? 'Meta+K' : 'Control+K'}
          className="h-9 gap-2 px-2.5 md:w-56 md:justify-start"
          onClick={() => setOpen(true)}
        >
          <SearchIcon aria-hidden />
          <span className="hidden font-normal text-muted-foreground md:inline">Buscar…</span>
          {/* Atalho só de enfeite (anunciado por aria-keyshortcuts): no conteúdo do CSS, fora do texto do botão. */}
          <kbd
            aria-hidden
            data-shortcut={shortcut}
            className="ml-auto hidden rounded border border-border bg-muted px-1.5 font-mono text-[0.6875rem] text-muted-foreground after:content-[attr(data-shortcut)] md:inline"
          />
        </Button>
      </Tooltip>
      {open ? <SearchDialog organizationId={organizationId} onClose={() => setOpen(false)} /> : null}
    </>
  );
}

function SearchDialog({ organizationId, onClose }: { organizationId: string; onClose: () => void }) {
  const { workspace, project } = useCurrentProject(organizationId);
  return (
    <Dialog
      open
      onOpenChange={(next) => !next && onClose()}
      title="Buscar"
      description={
        project ? (
          <>
            Objetivos, KPIs, iniciativas, reuniões e documentos do projeto{' '}
            <span className="font-semibold text-foreground">{project.name}</span>.
          </>
        ) : undefined
      }
      className="top-[8dvh] max-h-[min(84dvh,48rem)] w-[min(40rem,calc(100vw-2rem))] translate-y-0"
    >
      {workspace.isPending ? (
        <LoadingState rows={3} label="Carregando o projeto…" />
      ) : workspace.isError ? (
        <ErrorState
          error={workspace.error}
          onRetry={() => void workspace.refetch()}
          retrying={workspace.isFetching}
        />
      ) : project ? (
        <SearchPanel organizationId={organizationId} projectId={project.id} onNavigate={onClose} />
      ) : (
        <EmptyState
          icon={SearchX}
          title="Nenhum projeto BSC"
          description="A busca procura dentro de um projeto BSC. Quando a empresa tiver um projeto, ele aparece aqui."
        />
      )}
    </Dialog>
  );
}

function SearchPanel({
  organizationId,
  projectId,
  onNavigate,
}: {
  organizationId: string;
  projectId: string;
  onNavigate: () => void;
}) {
  const { client } = useAuth();
  const user = useCurrentUser();
  const navigate = useNavigate();
  const listId = useId();
  const [text, setText] = useState('');
  const [q, setQ] = useState('');
  const [active, setActive] = useState(0);
  // Enter antes de a busca do texto digitado chegar: abre o primeiro resultado dela quando chegar.
  const [openFirst, setOpenFirst] = useState(false);

  useEffect(() => {
    const next = text.trim();
    if (next === q) return;
    const timer = setTimeout(() => {
      setQ(next);
      setActive(0);
    }, 250);
    return () => clearTimeout(timer);
  }, [text, q]);

  // Mesma regra do servidor: até 100 caracteres e pelo menos uma palavra de 2 letras.
  const tooLong = q.length > 100;
  const valid = !tooLong && searchTokens(q).length > 0;
  const result = useQuery({
    queryKey: ['search', organizationId, projectId, q],
    queryFn: ({ signal }) =>
      client.request(`/organizations/${organizationId}/search`, {
        query: { projectId, q },
        schema: searchResultSchema,
        signal,
      }),
    enabled: valid,
    staleTime: 30_000,
    meta: { handlesError: true },
  });

  const can = (module: ModuleKey) => canAccess(user, module);
  // Só os resultados do texto digitado: enquanto ele não vira a busca atual (espera de digitar) ou a consulta
  // carrega, nada antigo fica escolhível.
  const pending = text.trim() !== q;
  const showing = !pending && valid && result.isSuccess;
  const groups = (showing ? (result.data?.groups ?? []) : []).map((g) => ({
    ...g,
    items: g.items.flatMap((item) => {
      const to = searchItemPath(organizationId, projectId, item, can);
      return to ? [{ item, to }] : [];
    }),
  }));
  const flat = groups.flatMap((g) => g.items);
  const current = Math.min(active, Math.max(flat.length - 1, 0));
  const optionId = (index: number) => `${listId}-opcao-${index}`;

  useEffect(() => {
    if (!flat.length) return;
    document.getElementById(optionId(current))?.scrollIntoView?.({ block: 'nearest' });
  });

  const go = (entry: { item: SearchItem; to: string }) => {
    onNavigate();
    void navigate(entry.to);
  };

  const firstTo = flat[0]?.to;
  // Desiste de abrir só quando a tentativa atual falhou (um erro antigo do cache sendo buscado de novo não conta).
  const failed = (result.isError && !result.isFetching) || !valid;
  useEffect(() => {
    if (!openFirst || pending) return;
    if (showing) {
      setOpenFirst(false);
      if (firstTo) {
        onNavigate();
        void navigate(firstTo);
      }
    } else if (failed) {
      setOpenFirst(false);
    }
  }, [openFirst, pending, showing, failed, firstTo, onNavigate, navigate]);

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const entry = flat[current];
      if (entry) {
        go(entry);
        return;
      }
      // Sem o resultado do texto digitado ainda (espera de digitar ou buscando): busca na hora, se preciso, e abre o
      // primeiro resultado quando ele chegar.
      if (pending) {
        setQ(text.trim());
        setActive(0);
      }
      setOpenFirst(true);
      return;
    }
    if (!flat.length) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((current + 1) % flat.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((current - 1 + flat.length) % flat.length);
    }
  };

  let index = 0;
  return (
    <div className="flex flex-col gap-4">
      <div className="relative">
        <SearchIcon
          aria-hidden
          className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground"
        />
        <Input
          autoFocus
          type="search"
          role="combobox"
          aria-label="O que você procura?"
          aria-expanded={flat.length > 0}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={flat.length ? optionId(current) : undefined}
          aria-invalid={tooLong || undefined}
          placeholder="Ex.: receita, RAC agosto, planejamento comercial"
          maxLength={100}
          className="pl-9"
          value={text}
          onChange={(e) => {
            setText(e.target.value);
            setOpenFirst(false);
          }}
          onKeyDown={onKeyDown}
        />
      </div>

      {!q ? (
        <p className="text-sm text-muted-foreground">
          Digite pelo menos 2 letras. A busca não diferencia acentos e tolera pequenos erros de digitação. Use
          ↑ e ↓ para escolher e Enter para abrir.
        </p>
      ) : tooLong ? (
        <p role="alert" className="text-sm text-status-red-fg">
          Busca longa demais: use até 100 caracteres.
        </p>
      ) : !valid ? (
        <p className="text-sm text-muted-foreground">Digite pelo menos 2 letras.</p>
      ) : result.isError ? (
        <ErrorState
          error={result.error}
          title="Não foi possível buscar"
          onRetry={() => void result.refetch()}
          retrying={result.isFetching}
          className="py-6"
        />
      ) : !showing ? (
        <LoadingState rows={3} label="Buscando…" />
      ) : flat.length === 0 ? (
        <EmptyState
          icon={SearchX}
          title="Nada encontrado"
          description={`Nenhum resultado para “${q}” neste projeto. Confira a grafia ou tente outra palavra.`}
          className="py-6"
        />
      ) : null}

      <p className="sr-only" aria-live="polite">
        {showing ? (flat.length === 1 ? '1 resultado' : `${flat.length} resultados`) : ''}
      </p>
      <div
        id={listId}
        role="listbox"
        aria-label="Resultados da busca"
        className={cn(!flat.length && 'hidden')}
      >
        {groups
          .filter((g) => g.items.length > 0)
          .map((g) => {
            const Icon = TYPE_ICONS[g.type];
            const headingId = `${listId}-${g.type}`;
            return (
              <div key={g.type} role="group" aria-labelledby={headingId} className="mb-3 last:mb-0">
                <div className="mb-1 flex items-baseline justify-between gap-2 px-2 text-xs font-semibold tracking-wide text-muted-foreground uppercase">
                  <span id={headingId}>{g.label}</span>
                  <span className="font-normal normal-case tabular-nums">
                    {g.total > g.items.length ? `${g.items.length} de ${g.total}` : g.total}
                  </span>
                </div>
                {g.items.map((entry) => {
                  const i = index++;
                  return (
                    <div
                      key={entry.item.id}
                      id={optionId(i)}
                      role="option"
                      aria-selected={i === current}
                      tabIndex={-1}
                      className={cn(
                        'flex cursor-pointer items-start gap-3 rounded-md px-2 py-2 text-sm',
                        i === current && 'bg-brand-soft',
                      )}
                      onMouseMove={() => i !== current && setActive(i)}
                      onClick={() => go(entry)}
                    >
                      <Icon aria-hidden className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
                      <span className="min-w-0 flex-1">
                        <span className="block truncate font-medium">{entry.item.title}</span>
                        {entry.item.subtitle ? (
                          <span className="block truncate text-xs text-muted-foreground">
                            {entry.item.subtitle}
                          </span>
                        ) : null}
                      </span>
                    </div>
                  );
                })}
                {g.total > g.items.length ? (
                  <p className="px-2 pt-1 text-xs text-muted-foreground">
                    Refine a busca para ver os outros {g.total - g.items.length}.
                  </p>
                ) : null}
              </div>
            );
          })}
      </div>
    </div>
  );
}
